import { DEFAULT_MIX_VOLUMES, type MixVolumes } from '../utils/mixVolumes';
import { previewCelestialChord } from '../audio/celestialPadEngine';
import { previewDrumSample } from '../audio/drumSamples';
import { previewBoomwhackerSample } from '../audio/boomwhackerSamples';

interface Props {
  open: boolean;
  volumes: MixVolumes;
  onChange: (next: MixVolumes) => void;
  onClose: () => void;
}

const CHANNELS: {
  key: keyof MixVolumes;
  label: string;
  icon: string;
  hint: string;
  preview: () => void;
}[] = [
  {
    key: 'boom',
    label: 'Boomwhackers',
    icon: '🎵',
    hint: 'Melodía de colores',
    preview: () => previewBoomwhackerSample('do'),
  },
  {
    key: 'drums',
    label: 'Percusión',
    icon: '🥁',
    hint: 'Batería y ritmo',
    preview: () => previewDrumSample('kick'),
  },
  {
    key: 'harmony',
    label: 'Armonía',
    icon: '🎹',
    hint: 'Piano eléctrico',
    preview: () => previewCelestialChord('do'),
  },
];

export function MixVolumePanel({ open, volumes, onChange, onClose }: Props) {
  if (!open) return null;

  const setChannel = (key: keyof MixVolumes, value: number) => {
    onChange({ ...volumes, [key]: value });
  };

  return (
    <div className="mix-volume-backdrop" role="presentation" onClick={onClose}>
      <div
        className="mix-volume-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mix-volume-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mix-volume-panel__header">
          <div>
            <h2 id="mix-volume-title" className="mix-volume-panel__title">
              Nivelar volúmenes
            </h2>
            <p className="mix-volume-panel__subtitle">
              Ajusta boomwhackers, percusión y armonía para que suenen equilibrados.
            </p>
          </div>
          <button type="button" className="mix-volume-panel__close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>

        <div className="mix-volume-panel__channels">
          {CHANNELS.map((ch) => (
            <div key={ch.key} className="mix-volume-channel">
              <div className="mix-volume-channel__head">
                <span className="mix-volume-channel__icon" aria-hidden="true">
                  {ch.icon}
                </span>
                <div>
                  <span className="mix-volume-channel__label">{ch.label}</span>
                  <span className="mix-volume-channel__hint">{ch.hint}</span>
                </div>
                <strong className="mix-volume-channel__value">{volumes[ch.key]}%</strong>
              </div>
              <input
                type="range"
                className="mix-volume-channel__slider"
                min={0}
                max={100}
                step={5}
                value={volumes[ch.key]}
                aria-label={`Volumen ${ch.label}`}
                onChange={(e) => setChannel(ch.key, Number(e.target.value))}
                onMouseUp={ch.preview}
                onTouchEnd={ch.preview}
              />
            </div>
          ))}
        </div>

        <footer className="mix-volume-panel__footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onChange({ ...DEFAULT_MIX_VOLUMES })}
          >
            Restaurar niveles
          </button>
          <button type="button" className="btn btn-play" onClick={onClose}>
            Listo
          </button>
        </footer>
      </div>
    </div>
  );
}
